/**
 * Main configuration file for republic-specific settings.
 * This file serves as a barrel export, assembling configurations from section-based files.
 *
 * All existing imports will continue to work without changes.
 */

import type { Repub } from '@cx-modules/constants';

import { PLUS_CONFIGS } from './product-plus/plus-config';
import { FAVICON_CONFIGS } from './favicon-config';
import { TRACKING_CONFIGS } from './tracking-config';
import type { RepubConfig, SupportedRepubName } from './types';

// Re-export all types
export type { RepubConfig, SupportedRepubName } from './types';

export { type AuthProvider, authProviders } from './auth-provider';
export { SHOW_AGREE_TERMS_AS_CHECKBOX_CONFIG, SIGN_IN_IMMEDIATELY_AFTER_SIGNUP_CONFIG } from './non-plus-auth-config';
export { type NonPlusAuthUrlsConfig, NON_PLUS_AUTH_URLS_CONFIG } from './non-plus-auth-urls-config';
export { SHOW_MARKETING_CONSENT_CONFIG } from './marketing-consent-config';
export { PLUS_CONFIGS, type PlusConfig } from './product-plus/plus-config';
export { STRIPE_PRODUCT_IDS, type ProductId } from './product-plus/stripe-constants';

/**
 * Main configuration object mapping republic names to their configurations
 * Assembled from individual section configs
 *
 * Note: Typed as Partial<Record<Repub, RepubConfig>> to allow indexing with any Repub value,
 * but only contains configurations for SupportedRepubName values.
 */
export const REPUB_CONFIG: Partial<Record<Repub, RepubConfig>> & Record<SupportedRepubName, RepubConfig> = {
  WS: {
    favicon: FAVICON_CONFIGS.WS,
    plus: PLUS_CONFIGS.WS,
    tracking: TRACKING_CONFIGS.WS,
  },
  OC: {
    favicon: FAVICON_CONFIGS.OC,
    plus: PLUS_CONFIGS.OC,
    tracking: TRACKING_CONFIGS.OC,
  },
  US: {
    favicon: FAVICON_CONFIGS.US,
    plus: PLUS_CONFIGS.US,
    tracking: TRACKING_CONFIGS.US,
  },
  GZ: {
    favicon: FAVICON_CONFIGS.GZ,
    plus: PLUS_CONFIGS.GZ,
    tracking: TRACKING_CONFIGS.GZ,
  },
};

export function isSupportedRepub(repub: string): repub is SupportedRepubName {
  return Object.keys(REPUB_CONFIG).includes(repub);
}
